import type { Config } from '@/workflow-runner/_contract/config';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import { conceptToolHandles } from '@/workflow-runner/conceptToolTypes';
import { INVOCATION_STAGES, INVOCATION_STAGE_MS } from '@/workflow-runner/conceptToolInvocation';

type Clock = {
    now: () => number;
    schedule: (callback: () => void, milliseconds: number) => ReturnType<typeof setTimeout>;
    cancelTimer: (timer: ReturnType<typeof setTimeout>) => void;
};
const SUM_INTERVAL_MS = 1800;
export const TOOL_INTERVAL_MS = 3500;

export function createConceptCyclePlaybackOwner(clock: Clock = {
    now: () => performance.now(),
    schedule: (callback, milliseconds) => setTimeout(callback, milliseconds),
    cancelTimer: timer => clearTimeout(timer),
}) {
    let key: string | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let startedAt = 0;
    let remaining = SUM_INTERVAL_MS;
    let generation = 0;
    const stop = () => {
        generation++;
        if (timer !== undefined) {
            clock.cancelTimer(timer);
            timer = undefined;
            remaining = Math.max(0, remaining - (clock.now() - startedAt));
        }
    };
    const synchronize = (args: {
        config: Config; runtimeState: RuntimeState; cosmosData: CosmosDataModel;
        playbackActive?: boolean; animationSuspended?: boolean;
    }) => {
        const { runtimeState: state } = args;
        const playback = state.episode;
        const content = state.conceptArrangement?.content;
        const tour = state.conceptArrangement?.content === 'tool-invocation' && state.conceptArrangement.invocationStage === 'tour';
        const cycling = (content === 'sum-resources' || content === 'tool-types' || tour) && playback;
        const interval = tour ? INVOCATION_STAGE_MS : content === 'tool-types' ? TOOL_INTERVAL_MS : SUM_INTERVAL_MS;
        const nextKey = cycling
            ? `${playback.episodeHandle}:${state.sceneIndex}:${playback.beatIndex}:${playback.revision}` : undefined;
        if (nextKey !== key) {
            stop();
            key = nextKey;
            remaining = interval;
        }
        if (!cycling || !playback.isPlaying || playback.error || args.animationSuspended || args.playbackActive) {
            stop();
            return;
        }
        if (timer !== undefined) return;
        const update = args.config.runtimeCallbacks?.onRuntimeStateChange;
        if (!update) throw new Error('Concept cycle playback requires a runtime update callback.');
        const toolCount = content === 'tool-types' || tour ? conceptToolHandles(args.cosmosData).length : 0;
        const isRunner = (current: object): current is RuntimeState =>
            'adapterMode' in current && current.adapterMode === 'runner';
        const token = generation;
        const tick = () => {
            if (token !== generation) return;
            timer = undefined;
            remaining = interval;
            update(current => {
                if (token !== generation) return current;
                if (!isRunner(current)) return current;
                const active = current.episode;
                if (current.conceptArrangement?.content !== content
                    || !active?.isPlaying || active.error || active.revision !== playback.revision
                    || active.episodeHandle !== playback.episodeHandle || active.beatIndex !== playback.beatIndex
                    || current.sceneIndex !== state.sceneIndex) return current;
                if (content === 'tool-types') {
                    const index = current.conceptTool?.revision === active.revision ? current.conceptTool.index : 0;
                    return { ...current, conceptTool: { revision: active.revision, index: (index + 1) % toolCount } };
                }
                if (tour) {
                    if (current.conceptArrangement?.content !== 'tool-invocation'
                        || current.conceptArrangement.invocationStage !== 'tour') return current;
                    const progress = current.conceptInvocation?.revision === active.revision ? current.conceptInvocation : undefined;
                    const stageIndex = ((progress?.stageIndex ?? 0) + 1) % INVOCATION_STAGES.length;
                    const index = ((progress?.index ?? 0) + (stageIndex === 0 ? 1 : 0)) % toolCount;
                    return { ...current, conceptInvocation: { revision: active.revision, index, stageIndex } };
                }
                const alternative = current.conceptSum?.revision === active.revision ? current.conceptSum.alternative : 0;
                return { ...current, conceptSum: { revision: active.revision, alternative: alternative === 0 ? 1 : 0 } };
            });
            if (token === generation && timer === undefined) schedule();
        };
        const schedule = () => {
            startedAt = clock.now();
            timer = clock.schedule(tick, remaining);
        };
        schedule();
    };
    return Object.assign(synchronize, { dispose: () => { stop(); key = undefined; remaining = SUM_INTERVAL_MS; } });
}
